import React, { useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Flag } from 'lucide-react';

/**
 * Size class-sets. 'auto' = viewport-responsive (the live carousel). 'desktop'/'mobile'
 * pin one breakpoint's styles regardless of the viewer's screen — REQUIRED for previews:
 * Tailwind md: classes respond to the VIEWPORT, not the container, so a small preview
 * box on a desktop screen would otherwise render desktop-sized text in a miniature box
 * (the "preview ≠ reality" bug, round 2).
 */
const SIZE_CLASSES = {
  auto: {
    textPos: 'bottom-2 left-3 right-28 md:bottom-4 md:left-4 md:right-40',
    headline: 'text-sm md:text-xl',
    detail: 'text-[11px] md:text-sm',
    detailMt: 'mt-0.5 md:mt-1',
    label: 'text-[10px] md:text-xs',
    cta: 'bottom-2 right-2 md:bottom-4 md:right-3 text-[11px] md:text-sm px-3 py-1.5 md:px-4 md:py-2',
  },
  desktop: {
    textPos: 'bottom-4 left-4 right-40',
    headline: 'text-xl',
    detail: 'text-sm',
    detailMt: 'mt-1',
    label: 'text-xs',
    cta: 'bottom-4 right-3 text-sm px-4 py-2',
  },
  mobile: {
    textPos: 'bottom-2 left-3 right-28',
    headline: 'text-sm',
    detail: 'text-[11px]',
    detailMt: 'mt-0.5',
    label: 'text-[10px]',
    cta: 'bottom-2 right-2 text-[11px] px-3 py-1.5',
  },
};

/**
 * BannerCreative — the ONE shared renderer for a banner's visual composite
 * (image + scrim + tag + headline + detail + label + CTA pill + report flag).
 *
 * Used by: the live carousel slide (BannerCarousel), the create-wizard live preview
 * (BannerCreateModal), the employer edit modal (BannerEditModal), and the admin
 * platform-banner modal (AdminBannerFormModal). Because every surface renders THIS
 * component, the preview an employer approves is pixel-identical to what publishes
 * (spec §6 — the v1 "preview ≠ reality" bug is closed structurally).
 *
 * Purely presentational: no fetching, no navigation, no click-to-open behavior —
 * the parent owns sizing (height/aspect container), clicks, and keyboard handling.
 *
 * Props (camelCase — parents consuming the snake_case feed must map explicitly):
 *  - imageUrl, imagePosition (0-100 vertical focal %, default 50)
 *  - headline, detail, label  (plain text; React escapes on render)
 *  - tag: 'sponsored' | 'featured'   (featured = admin platform banner, gold)
 *  - cta: 'apply' | 'learn' | null   (null hides the pill)
 *  - showReport, isReported, onReport (report flag; hidden for platform banners)
 */
const BannerCreative = ({
  imageUrl,
  imagePosition = 50,
  headline = '',
  detail = '',
  label = '',
  tag = 'sponsored',
  cta = 'apply',
  showReport = false,
  isReported = false,
  onReport,
  size = 'auto',
}) => {
  const { t } = useTranslation();
  const S = SIZE_CLASSES[size] || SIZE_CLASSES.auto;
  const isFeatured = tag === 'featured';
  const tagText = isFeatured ? t('banner.featured', 'Featured') : t('banner.sponsored', 'Sponsored');
  // Closed CTA set — mirrors banner_service.CTA_KINDS; labels are i18n'd, never free text.
  const CTA_TEXT = {
    apply: ['banner.applyNow', 'Apply now'],
    learn: ['banner.learnMore', 'Learn more'],
    register: ['banner.registerNow', 'Register now'],
    signup: ['banner.signUpNow', 'Sign up now'],
    join: ['banner.joinNow', 'Join now'],
  };
  const [ctaKey, ctaDefault] = CTA_TEXT[cta] || CTA_TEXT.learn;
  const ctaText = t(ctaKey, ctaDefault);

  return (
    <div className="relative w-full h-full overflow-hidden" data-testid="banner-creative">
      {imageUrl ? (
        <img
          src={imageUrl}
          alt={label || tagText}
          className="w-full h-full object-cover select-none"
          style={{ objectPosition: `center ${imagePosition}%` }}
          loading="eager"
        />
      ) : (
        <div className="w-full h-full bg-gray-200" />
      )}

      {/* Scrim keeps the overlay text readable over any image */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/5 to-black/25 pointer-events-none" />

      {/* Tag pill (Sponsored = paid employer; Featured = official platform, gold) */}
      <span
        className={`absolute top-2 left-2 text-[10px] font-bold uppercase tracking-wide rounded-full px-2 py-0.5 text-white ${
          isFeatured ? 'bg-gradient-to-br from-[#F5A623] to-[#E0820A]' : 'bg-black/60'
        }`}
      >
        {tagText}
      </span>

      {/* Report affordance — employer banners only; parent stops propagation */}
      {showReport && (
        <button
          type="button"
          onClick={onReport}
          disabled={isReported}
          aria-label={t('banner.reportTooltip', 'Report this banner')}
          title={t('banner.reportTooltip', 'Report this banner')}
          className="absolute top-2 right-2 flex items-center justify-center w-6 h-6 rounded-full bg-black/40 text-white/90 hover:bg-black/60 disabled:opacity-50 transition-colors"
        >
          <Flag size={12} />
        </button>
      )}

      {/* Headline + detail + label — bottom-left, clear of the CTA pill */}
      <div className={`absolute ${S.textPos} text-white pointer-events-none`}>
        {headline && (
          <p className={`font-bold ${S.headline} leading-tight drop-shadow-md line-clamp-2 m-0`}>
            {headline}
          </p>
        )}
        {detail && (
          <p className={`${S.detail} font-semibold opacity-95 drop-shadow-md truncate m-0 ${S.detailMt}`}>
            {detail}
          </p>
        )}
        {label && (
          <p className={`${S.label} font-medium opacity-80 drop-shadow-md truncate m-0 mt-0.5`}>
            {label}
          </p>
        )}
      </div>

      {/* CTA pill — bottom-right; visual affordance only (parent owns the click) */}
      {cta && (
        <span className={`absolute ${S.cta} bg-white text-gray-900 font-bold rounded-full shadow-lg pointer-events-none`}>
          {ctaText}
        </span>
      )}
    </div>
  );
};

/**
 * BannerPreview — a pixel-faithful MINIATURE of the live carousel slot.
 *
 * Renders BannerCreative at the carousel's REAL dimensions (desktop: 1232×280 —
 * max-w-7xl minus px-4 gutters at h-[280px]; mobile: 351×160 — 375px viewport minus
 * px-3 gutters at h-[160px]) and shrinks it with transform:scale() to fit the host
 * width. Because it's a true scaled render (not a re-layout), text size, wrapping,
 * spacing, and crop are IDENTICAL to what publishes — the fix for the wizard preview
 * showing oversized, clipped text (fonts don't scale with a smaller box otherwise).
 */
const PREVIEW_STAGE = {
  desktop: { width: 1232, height: 280 },
  mobile: { width: 351, height: 160 },
};

export const BannerPreview = ({ mode = 'desktop', className = '', ...creativeProps }) => {
  const hostRef = useRef(null);
  const [hostWidth, setHostWidth] = useState(0);

  useLayoutEffect(() => {
    const el = hostRef.current;
    if (!el) return undefined;
    // offsetWidth = LAYOUT width — immune to the dialog's entrance transform
    // (getBoundingClientRect is transform-scaled and mis-measured mid-animation,
    // which is how the live preview ended up rendering unscaled at full size).
    const measure = () => setHostWidth(el.offsetWidth || 0);
    measure();
    if (typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(measure);
      ro.observe(el);
      return () => ro.disconnect();
    }
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  const stage = PREVIEW_STAGE[mode] || PREVIEW_STAGE.desktop;
  // Clamp: shrink-only. A mis-measure must never ENLARGE the composite beyond the
  // real banner (the live-bug symptom was a preview bigger than the modal itself).
  const scale = hostWidth > 0 ? Math.min(1, hostWidth / stage.width) : 0;

  return (
    <div
      ref={hostRef}
      className={`relative overflow-hidden rounded-lg border border-border bg-muted min-w-0 w-full ${className}`}
      style={{ height: scale > 0 ? Math.round(stage.height * scale) : 40 }}
      data-testid={`banner-preview-${mode}`}
    >
      {scale > 0 && (
        <div
          // ABSOLUTE: removed from layout flow, so the full-size stage can never
          // stretch the host/dialog or create scrollbars — it only paints, clipped
          // by the host's overflow-hidden, scaled to fit.
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: stage.width,
            height: stage.height,
            transform: `scale(${scale})`,
            transformOrigin: 'top left',
          }}
        >
          <BannerCreative {...creativeProps} size={mode} />
        </div>
      )}
    </div>
  );
};

export default BannerCreative;
